import { Building2 } from 'lucide-react';
import { typedApi } from '../../api/typed';
import { useResource } from '../../hooks/useResource';

function intensityLevel(pressure, maxPressure) {
  if (pressure > maxPressure * 0.7) return 'HIGH';
  if (pressure > maxPressure * 0.4) return 'MED';
  return 'LOW';
}

function intensityColor(level, fallback) {
  if (level === 'HIGH') return 'var(--pd-crimson)';
  if (level === 'MED') return 'var(--pd-warning)';
  return fallback || 'var(--pd-subtle)';
}

function budgetColor(percentUsed) {
  if (percentUsed > 100) return 'var(--pd-crimson)';
  if (percentUsed > 80) return 'var(--pd-warning)';
  return 'var(--pd-subtle)';
}

export default function PendencyRadar({ businessPressure, activeBusiness, onBusinessFilter }) {
  const { data: budgetsData } = useResource(
    'pendency-radar-budgets',
    async ({ signal }) => await typedApi.get('/api/attention-budget', { signal }),
    { initialData: { budgets: [] } },
  );
  const budgets = budgetsData?.budgets || [];

  if (!businessPressure || businessPressure.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full px-4 text-center"
        style={{ color: 'var(--pd-subtle)' }}>
        <div className="pd-empty-icon w-12 h-12 rounded-2xl flex items-center justify-center mb-3"
          style={{ background: 'var(--pd-panel-2)', border: '1px solid var(--pd-border-subtle)' }}>
          <Building2 className="w-5 h-5" />
        </div>
        <span className="text-[11px] font-medium" style={{ color: 'var(--pd-muted)' }}>No businesses yet</span>
        <span className="text-[9px] mt-1" style={{ color: 'var(--pd-subtle)' }}>Add a business to see pressure metrics</span>
      </div>
    );
  }

  const maxPressure = Math.max(...businessPressure.map(b => b.pressure), 1);
  const budgetMap = Object.fromEntries(budgets.map(b => [b.businessId, b]));

  const BudgetRing = ({ pct, color, size = 28 }) => {
    const r = (size - 4) / 2;
    const c = 2 * Math.PI * r;
    const dashOffset = c * (1 - Math.min(pct, 100) / 100);
    const isOver = pct > 100;
    return (
      <svg width={size} height={size} className="-rotate-90 flex-shrink-0">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--pd-border)" strokeWidth="2.5" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke={isOver ? 'var(--pd-crimson)' : pct > 80 ? 'var(--pd-warning)' : color}
          strokeWidth="2.5" strokeDasharray={c} strokeDashoffset={dashOffset}
          strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.5s ease' }} />
      </svg>
    );
  };

  return (
    <div data-testid="pendency-radar" className="flex flex-col h-full overflow-hidden"
      style={{ borderRight: '1px solid var(--pd-border)', background: 'var(--pd-panel)' }}>

      <div className="px-4 pt-4 pb-2" style={{ borderBottom: '1px solid var(--pd-border-subtle)' }}>
        <div className="text-[10px] uppercase tracking-[0.1em] font-semibold"
          style={{ color: 'var(--pd-subtle)' }}>Pendency Radar</div>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar px-3 py-3 space-y-1.5">
        <button data-testid="radar-biz-all"
          onClick={() => onBusinessFilter?.(null)}
          className="w-full text-left px-3 py-2 rounded-xl transition-colors"
          style={{
            background: !activeBusiness ? 'var(--pd-panel-2)' : 'transparent',
            border: `1px solid ${!activeBusiness ? 'var(--pd-border)' : 'var(--pd-border-subtle)'}`,
          }}>
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full" style={{ background: !activeBusiness ? 'var(--pd-text)' : 'var(--pd-muted)', opacity: !activeBusiness ? 1 : 0.4 }} />
            <span className="text-[12px] font-semibold" style={{ color: !activeBusiness ? 'var(--pd-text)' : 'var(--pd-muted)' }}>All businesses</span>
          </div>
        </button>
        {businessPressure.map(biz => {
          const isActive = activeBusiness?.id === biz.id;
          const barWidth = Math.max(8, (biz.pressure / maxPressure) * 100);
          const intensity = intensityLevel(biz.pressure, maxPressure);
          const budget = budgetMap[biz.id];
          const hasBudget = budget && budget.budgetHours > 0;

          return (
            <button key={biz.id} data-testid={`radar-biz-${(biz.shortCode || '').toLowerCase()}`}
              onClick={() => onBusinessFilter?.(isActive ? null : biz)}
              className="w-full text-left px-3 py-2.5 rounded-xl transition-colors"
              style={{
                background: isActive ? `${biz.color}12` : 'transparent',
                border: `1px solid ${isActive ? `${biz.color}33` : 'var(--pd-border-subtle)'}`,
              }}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  {hasBudget ? (
                    <BudgetRing pct={budget.percentUsed} color={biz.color} />
                  ) : (
                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: biz.color }} />
                  )}
                  <span className="text-[12px] font-semibold" style={{ color: 'var(--pd-text)' }}>
                    {biz.shortCode}
                  </span>
                </div>
                <span className="text-[10px] tabular-nums font-medium"
                  style={{ color: intensityColor(intensity, 'var(--pd-subtle)') }}>
                  {biz.tasks} tasks
                </span>
              </div>

              <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--pd-panel-2)' }}>
                <div className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${barWidth}%`,
                    background: intensityColor(intensity, biz.color),
                  }} />
              </div>

              <div className="flex items-center gap-2 mt-1.5 text-[9px] flex-wrap" style={{ color: 'var(--pd-subtle)' }}>
                {biz.urgent > 0 && <span style={{ color: 'var(--pd-crimson)' }}>{biz.urgent} urgent</span>}
                {biz.calls > 0 && <span>{biz.calls} calls</span>}
                {biz.payments > 0 && <span>{biz.payments} pay</span>}
                {hasBudget ? (
                  <span className="ml-auto tabular-nums" style={{
                    color: budgetColor(budget.percentUsed)
                  }}>
                    {budget.usedHours}h / {budget.budgetHours}h
                  </span>
                ) : (
                  <span className="ml-auto tabular-nums">
                    {biz.totalMinutes >= 60 ? `${Math.floor(biz.totalMinutes / 60)}h ${biz.totalMinutes % 60}m` : `${biz.totalMinutes}m`}
                  </span>
                )}
              </div>

              {hasBudget && budget.percentUsed > 80 && (
                <div data-testid={`budget-warning-${biz.id}`}
                  className="mt-1.5 text-[8px] px-2 py-1 rounded-lg font-semibold"
                  style={{
                    background: budget.percentUsed > 100 ? 'rgba(180,35,58,0.08)' : 'rgba(180,83,9,0.08)',
                    color: budget.percentUsed > 100 ? 'var(--pd-crimson)' : 'var(--pd-warning)',
                  }}>
                  {budget.percentUsed > 100
                    ? `Over budget by ${budget.usedHours - budget.budgetHours}h`
                    : `${Math.round(100 - budget.percentUsed)}% budget left · ${budget.daysLeft}d remaining`}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
